import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useCreateCourse } from '../SchoolYearView/useCreateCourse';
import type { ClassOption, CourseOption, TermOption } from './ScheduleBlockDialog';
import { useCreateClassOffering } from './useCreateClassOffering';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string;
  courses: ReadonlyArray<CourseOption>;
  terms: ReadonlyArray<TermOption>;
  onCreated: (classOffering: ClassOption) => void;
  onOptionsRefresh?: () => void | Promise<void>;
  onRefreshError?: (message: string) => void;
  configureTermsHref?: string;
}

function normalizedSection(value: string) {
  return value.trim().toUpperCase();
}

export default function ClassOfferingDialog({
  open,
  onOpenChange,
  schoolYearId,
  courses,
  terms,
  onCreated,
  onOptionsRefresh,
  onRefreshError,
  configureTermsHref,
}: Props) {
  const openSession = useRef(false);
  const sessionGeneration = useRef(0);
  const sessionIdentity = useRef({ open: false, schoolYearId: '' });
  if (
    sessionIdentity.current.open !== open ||
    sessionIdentity.current.schoolYearId !== schoolYearId
  ) {
    sessionIdentity.current = { open, schoolYearId };
    sessionGeneration.current += 1;
  }
  const [availableCourses, setAvailableCourses] = useState<CourseOption[]>([]);
  const [courseId, setCourseId] = useState('');
  const [termId, setTermId] = useState('');
  const [section, setSection] = useState('A');
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [savingCourse, setSavingCourse] = useState(false);
  const [savingClass, setSavingClass] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [createCourse] = useCreateCourse();
  const [createClassOffering] = useCreateClassOffering();

  useEffect(() => {
    if (!open) {
      openSession.current = false;
      setSavingCourse(false);
      setSavingClass(false);
      return;
    }
    if (openSession.current) {
      setAvailableCourses((current) => {
        const byId = new Map(current.map((course) => [course.id, course]));
        courses.forEach((course) => byId.set(course.id, course));
        return [...byId.values()];
      });
      return;
    }

    openSession.current = true;
    setAvailableCourses([...courses]);
    setCourseId(courses[0]?.id ?? '');
    setTermId(terms[0]?.id ?? '');
    setSection('A');
    setCreatingCourse(courses.length === 0);
    setCourseName('');
    setSavingCourse(false);
    setSavingClass(false);
    setError(null);
    setWarning(null);
  }, [courses, open, terms]);

  const refreshOptions = async (
    savedLabel: string,
    actionSession: number,
    showLocalWarning = true,
  ) => {
    if (!onOptionsRefresh) return;
    try {
      await onOptionsRefresh();
    } catch (refreshError) {
      if (sessionGeneration.current !== actionSession) return;
      const detail = refreshError instanceof Error && refreshError.message ? `: ${refreshError.message}` : '';
      const message = `${savedLabel}, but course options could not be refreshed${detail}. The saved item remains selected.`;
      if (showLocalWarning) setWarning(message);
      onRefreshError?.(message);
    }
  };

  const handleCreateCourse = async () => {
    if (!courseName.trim()) return;
    const actionSession = sessionGeneration.current;
    setSavingCourse(true);
    setError(null);
    setWarning(null);

    let result;
    try {
      // Intentionally omit terms. The exact class mutation below adds one
      // CourseTerm and one section without invoking the broad generator.
      result = await createCourse({
        variables: {
          input: {
            schoolYearId,
            name: courseName.trim(),
            defaultSections: 1,
            termIds: [],
          },
        },
      });
    } catch (caught) {
      if (sessionGeneration.current !== actionSession) return;
      setError(caught instanceof Error ? caught.message : 'Unable to create this course.');
      setSavingCourse(false);
      return;
    }

    if (sessionGeneration.current !== actionSession) return;
    const errors = result.data?.createCourse?.userErrors ?? [];
    const course = result.data?.createCourse?.course;
    if (errors.length > 0 || !course) {
      setError(errors.length > 0 ? errors.map((item) => item.message).join(', ') : 'The course was not returned.');
      setSavingCourse(false);
      return;
    }

    const savedCourse = { id: course.id, name: course.name };
    setAvailableCourses((current) => [...current.filter((item) => item.id !== savedCourse.id), savedCourse]);
    setCourseId(savedCourse.id);
    setCreatingCourse(false);
    setCourseName('');
    setSavingCourse(false);
    await refreshOptions('Course created', actionSession);
  };

  const handleCreateClass = async () => {
    const normalized = normalizedSection(section);
    if (!courseId || !termId || !normalized) return;
    const actionSession = sessionGeneration.current;
    setSavingClass(true);
    setError(null);
    setWarning(null);

    let result;
    try {
      result = await createClassOffering({
        variables: { input: { schoolYearId, courseId, termId, section: normalized } },
      });
    } catch (caught) {
      if (sessionGeneration.current !== actionSession) return;
      setError(caught instanceof Error ? caught.message : 'Unable to create this class.');
      setSavingClass(false);
      return;
    }

    if (sessionGeneration.current !== actionSession) return;
    const errors = result.data?.createClassOffering?.userErrors ?? [];
    const classOffering = result.data?.createClassOffering?.classOffering;
    if (errors.length > 0 || !classOffering) {
      setError(errors.length > 0 ? errors.map((item) => item.message).join(', ') : 'The class was not returned.');
      setSavingClass(false);
      return;
    }

    const created: ClassOption = {
      id: classOffering.id,
      displayName: classOffering.displayName,
      courseId: classOffering.course.id,
      courseName: classOffering.course.name,
      section: classOffering.section,
      term: {
        id: classOffering.term.id,
        name: classOffering.term.name,
        startsOn: classOffering.term.startsOn == null ? null : String(classOffering.term.startsOn),
        endsOn: classOffering.term.endsOn == null ? null : String(classOffering.term.endsOn),
      },
    };
    // Retain and select the committed class before any parent refresh. A
    // refresh failure must never invite a duplicate class retry.
    onCreated(created);
    onOpenChange(false);
    setSavingClass(false);
    await refreshOptions('Class created', actionSession, false);
  };

  const busy = savingCourse || savingClass;
  const termsConfigured = terms.length > 0;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && busy) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create class</DialogTitle>
          <DialogDescription>Create one exact section for one course and term.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {error && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
          {warning && <div role="status" className="rounded-md bg-muted p-3 text-sm">{warning}</div>}

          {!termsConfigured ? (
            <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
              <p className="font-medium">Academic terms are required before a class can be created.</p>
              <p className="mt-1">Configure this school year in School Settings, then return here to create the exact class section.</p>
              {configureTermsHref && (
                <a className="mt-2 inline-block font-medium underline" href={configureTermsHref}>
                  Open School Years &amp; Terms
                </a>
              )}
            </div>
          ) : creatingCourse ? (
            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-2">
                <Label htmlFor="inline-course-name">Course name</Label>
                <Input id="inline-course-name" value={courseName} disabled={busy} onChange={(event) => setCourseName(event.target.value)} autoFocus />
              </div>
              <div className="flex justify-end gap-2">
                {availableCourses.length > 0 && <Button type="button" variant="outline" disabled={busy} onClick={() => setCreatingCourse(false)}>Cancel course</Button>}
                <Button type="button" disabled={savingCourse || !courseName.trim()} onClick={handleCreateCourse}>{savingCourse ? 'Creating course…' : 'Create course'}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label>Course</Label>
                <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setError(null); setCreatingCourse(true); }}>Create course</Button>
              </div>
              <Select value={courseId} onValueChange={setCourseId} disabled={busy}>
                <SelectTrigger aria-label="Course"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                <SelectContent>{availableCourses.map((course) => <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {termsConfigured && (
            <>
              <div className="space-y-2">
                <Label>Term</Label>
                <Select value={termId} onValueChange={setTermId} disabled={busy}>
                  <SelectTrigger aria-label="Class term"><SelectValue placeholder="Choose a term" /></SelectTrigger>
                  <SelectContent>{terms.map((term) => <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inline-class-section">Section</Label>
                <Input id="inline-class-section" value={section} maxLength={12} disabled={busy} onChange={(event) => setSection(event.target.value.toUpperCase())} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          {termsConfigured && (
            <Button type="button" onClick={handleCreateClass} disabled={creatingCourse || savingCourse || savingClass || !courseId || !termId || !normalizedSection(section)}>
              {savingClass ? 'Creating class…' : 'Create class'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
